import { AuthenticationTriggerWrapper } from '@/components/buttons/AuthenticationTriggerWrapper';
import { IMXPriceBreakdown } from '@/components/features/IMX/IMXPriceBreakdown';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { getChainCurrencies } from '@/utils/getChainCurrencies';
import { isImmutableChain } from '@/utils/immutable';
import { ReservoirAction } from '@/utils/reservoir';
import { Button, ButtonProps } from '@chakra-ui/react';
import { ListModal, ListStep, type useTokens } from '@sphere/reservoir-kit-ui';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { type FC, type ReactNode } from 'react';
import { toast } from 'sonner';
import ActionDisabledTooltip from './ActionDisabledTooltip';

type Props = ButtonProps & {
  token?: ReturnType<typeof useTokens>['data'][0];
  buttonChildren?: ReactNode;
  mutate?(): void;
  openState?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  onGoToToken?: () => void;
};

const List: FC<Props> = ({
  token,
  buttonChildren,
  mutate,
  openState,
  onGoToToken,
  ...buttonProps
}) => {
  const { t } = useTranslation('common');
  const marketplaceChain = useMarketplaceChain();
  const tokenId = token?.token?.tokenId;
  const contract = token?.token?.contract;

  const Trigger = (
    <ActionDisabledTooltip action={ReservoirAction.List}>
      <Button size="sm" variant="secondary" lineHeight={rem(28)} {...buttonProps}>
        {buttonChildren}
      </Button>
    </ActionDisabledTooltip>
  );

  return (
    <AuthenticationTriggerWrapper trigger={Trigger}>
      <ListModal
        trigger={Trigger}
        collectionId={contract}
        tokenId={tokenId}
        enableOnChainRoyalties={false}
        currencies={getChainCurrencies({ chainId: marketplaceChain.id, contract })}
        chainId={marketplaceChain.id}
        openState={openState}
        onClose={(_data, _stepData, currentStep) => {
          if (mutate && currentStep == ListStep.Complete) mutate();
        }}
        onListingComplete={() => {
          toast.success(t('toast.list.success.title'), {
            description: t('toast.list.success.description'),
          });
        }}
        onGoToToken={onGoToToken}
        onListingError={(err: any) => {
          if (err?.code === 4001) {
            toast.error(t('toast.general.transaction-cancelled.title'), {
              description: t('toast.general.transaction-cancelled.description'),
            });
            return;
          }
          toast.error(t('toast.list.transaction-incomplete.title'));
        }}
        {...(isImmutableChain(marketplaceChain.id) && {
          renderPriceBreakdown: props => <IMXPriceBreakdown {...props} />,
        })}
      />
    </AuthenticationTriggerWrapper>
  );
};

export default List;
